'use client'

/* eslint-disable react/hook-use-state */
import { noop } from 'lodash'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { UiState } from '@marketplace-web/shared/ui-state-util'

import { ConfigurationType } from '../types'
import { ItemUploadContextType } from '../types/context'
import { transformItemUploadConfigDto } from '../transformers/item-upload-configuration'
import { getItemUploadConfiguration } from '../api'

const initialConfiguration: ConfigurationType = {
  photoTip: null,
  catalogFieldsOrder: [],
  maxShippingPrice: null,
  abTests: {},
  firstTimeListingGuideline: null,
  uploadSessionId: '',
  priceRange: {
    minimum: { amount: '', currencyCode: '' },
    maximum: { amount: '', currencyCode: '' },
  },
  currency: '',
  isFirstTimeLister: null,
}

export const ItemUploadContext = createContext<ItemUploadContextType>({
  openPhotoInput: noop,
  fileInputRef: { current: null },
  isSecondDayLister: [false, noop],
  isPreviousLister: [false, noop],
  isFormDisabled: false,
  enableForm: noop,
  disableForm: noop,
  configuration: initialConfiguration,
  configurationRequestState: UiState.Idle,
  isABTestEnabled: () => false,
  getABTest: () => undefined,
  updateCurrency: noop,
})

export const ItemUploadContextProvider = ({ children }: { children: ReactNode }) => {
  const isSecondDayLister = useState(false)
  const isPreviousLister = useState(false)
  const [isFormDisabled, setIsFormDisabled] = useState(false)
  const [configuration, setConfiguration] = useState<ConfigurationType>(initialConfiguration)
  const [configurationRequestState, setConfigurationRequestState] = useState<UiState>(UiState.Idle)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleAddPhotosClick = useCallback(() => {
    const { current } = fileInputRef

    if (!current) return

    current.click()
    current.focus()
  }, [])

  const disableForm = useCallback(() => setIsFormDisabled(true), [])
  const enableForm = useCallback(() => setIsFormDisabled(false), [])

  const loadConfiguration = useCallback(async () => {
    setConfigurationRequestState(UiState.Pending)

    const response = await getItemUploadConfiguration()

    if ('errors' in response) {
      setConfigurationRequestState(UiState.Failure)

      return
    }

    const configurationModel = transformItemUploadConfigDto(response)

    setConfiguration(configurationModel)
    setConfigurationRequestState(UiState.Success)
  }, [])

  useEffect(() => {
    loadConfiguration()
  }, [loadConfiguration])

  const isABTestEnabled = useCallback(
    (testName: string) => {
      const test = configuration.abTests[testName]

      if (!test) return false

      return test.variant?.toLowerCase() === 'on'
    },
    [configuration],
  )

  const getABTest = useCallback(
    (testName: string) => configuration.abTests[testName],
    [configuration],
  )

  const updateCurrency = useCallback(
    (value: string) => {
      if (configuration.currency === value) return

      configuration.currency = value
    },
    [configuration],
  )

  const providerValue = useMemo(
    () => ({
      openPhotoInput: handleAddPhotosClick,
      isSecondDayLister,
      isPreviousLister,
      fileInputRef,
      isFormDisabled,
      enableForm,
      disableForm,
      configuration,
      configurationRequestState,
      isABTestEnabled,
      getABTest,
      updateCurrency,
    }),
    [
      handleAddPhotosClick,
      isSecondDayLister,
      isPreviousLister,
      isFormDisabled,
      enableForm,
      disableForm,
      configuration,
      configurationRequestState,
      isABTestEnabled,
      getABTest,
      updateCurrency,
    ],
  )

  return <ItemUploadContext.Provider value={providerValue}>{children}</ItemUploadContext.Provider>
}

export const useItemUploadContext = () => {
  const context = useContext(ItemUploadContext)

  if (!context) {
    throw new Error('useItemUploadContext must be used within a ItemUploadContext')
  }

  return context
}
